<script setup>
import { computed, ref, watch } from "vue";
import { useUserGroupStore } from "@/stores/useUserGroupStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";
import UserCardModal from "@/components/User/UserCardModal.vue";
import EditUserGroupForm from "./EditUserGroupForm.vue";
import DeleteUserGroupForm from "./DeleteUserGroupForm.vue";

// 1. Instância da API para montar URL absoluta da foto
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  userGroupId: { type: Number, required: true },
});

const emit = defineEmits(["close", "association-updated", "notify"]);

const userGroupStore = useUserGroupStore();
const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

// Estados para controle do Modal de Usuário
const showUserModal = ref(false);
const selectedUserId = ref(null);

// Estado para controle do EditUserGroupForm
const showEditModal = ref(false);

// Estado para controle do DeleteUserGroupForm
const showDeleteModal = ref(false);

function openUserModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserModal.value = true;
}

function closeUserModal() {
  showUserModal.value = false;
}

function openEditModal() {
  if (!association.value) return;
  userGroupStore.setSelectedAssociation(association.value);
  showEditModal.value = true;
}

function handleEditRefresh() {
  emit("association-updated");
}

function openDeleteModal() {
  if (!association.value) return;
  userGroupStore.setSelectedAssociation(association.value);
  showDeleteModal.value = true;
}

function handleDeleteClose() {
  showDeleteModal.value = false;
  emit("association-updated");
  emit("close");
}

function handleDeleteNotify(payload) {
  emit("notify", payload);
}

const association = computed(() => {
  // 1. Prioridade: objeto detalhado com auditoria (vindo de getAssociationById)
  if (
    userGroupStore.selectedAssociation &&
    userGroupStore.selectedAssociation.userGroupId === props.userGroupId
  ) {
    return userGroupStore.selectedAssociation;
  }

  // 2. Fallback: lista reduzida
  return (
    userGroupStore.associations.find(
      (a) => a.userGroupId === props.userGroupId
    ) || null
  );
});

watch(
  () => props.userGroupId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    try {
      // Quando existir o método, use aqui:
      // await userGroupStore.getAssociationById(newId);
      isLoadingData.value = false;
    } catch (e) {
      console.error("Erro ao carregar vínculo no CardModal:", e);
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

// ✅ normaliza usando baseURL da API e removendo /api
function normalizeAvatarPath(path) {
  if (!path) return "";

  // URL externa (social login, gravatar, etc.)
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  // base da API sem /api e sem barras extras
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");

  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

// ✅ SEM pravatar: se não tiver foto, devolve string vazia e usamos ícone no círculo
function getCreatorAvatarUrl(data) {
  if (!data) return "";
  const path =
    data.createdByUserPhotoPath || data.createdByUser?.photoPath || "";
  if (!path) return "";
  return normalizeAvatarPath(path);
}

function getUpdaterAvatarUrl(data) {
  if (!data) return "";
  const path =
    data.updatedByUserPhotoPath || data.updatedByUser?.photoPath || "";
  if (!path) return "";
  return normalizeAvatarPath(path);
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="usergroup-card-modal-content" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">Detalhes do Vínculo</h3>
        <button
          @click="emit('close')"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div v-if="association && !isLoadingData" class="card-content-grid">
        <div class="details-col">
          <div class="title-row">
            <h3 class="text-title-bold">
              {{ toTitleCase(association.userName) }}
              <span class="title-id">
                (ID Vínculo: {{ association.userGroupId }})
              </span>
            </h3>

            <button
              type="button"
              class="btn-edit-usergroup tooltip-bottom"
              data-tooltip="Editar vínculo"
              @click.stop="openEditModal"
            >
              <i class="fas fa-pencil-alt"></i>
            </button>

            <button
              type="button"
              class="btn-delete-usergroup tooltip-bottom"
              data-tooltip="Apagar vínculo"
              @click.stop="openDeleteModal"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>

          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">E-mail</span>
              <span class="meta-value">{{ association.email }}</span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Grupo Vinculado</span>
              <span class="meta-value group-value">
                {{ toTitleCase(association.groupName) }}
              </span>
            </div>

            <div class="meta-item meta-item-full">
              <span class="meta-label">Data de Associação</span>
              <span class="meta-value">
                {{ formatDateTimeForDisplay(association.addedAt) }}
              </span>
            </div>
          </div>

          <hr class="content-separator" />

          <div class="footer-info">
            <div class="users-row">
              <!-- Criador -->
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(association.createdByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getCreatorAvatarUrl(association)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getCreatorAvatarUrl(
                        association
                      )}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Criador</span>
                  <span class="chip-name">
                    {{ association.createdByUserName || "Não informado" }}
                  </span>
                  <span class="chip-date">
                    {{
                      formatDateTimeForDisplay(
                        association.createdAt || association.addedAt
                      )
                    }}
                  </span>
                </div>
              </div>

              <!-- Atualizador -->
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(association.updatedByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getUpdaterAvatarUrl(association)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getUpdaterAvatarUrl(
                        association
                      )}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Atualizador</span>
                  <span class="chip-name">
                    {{ association.updatedByUserName || "Não informado" }}
                  </span>
                  <span class="chip-date">
                    {{
                      formatDateTimeForDisplay(
                        association.updatedAt || association.addedAt
                      )
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes do vínculo...
      </div>

    </div>
  </div>

  <!-- Sub-modais via Teleport.
       Todos são position: fixed e o painel deste card ganha transform ao ser
       arrastado — um ancestral com transform viraria o containing block deles,
       fazendo o overlay cobrir só o painel e acompanhar o arrasto. Fora do
       painel no DOM, seguem ancorados na viewport. -->
  <Teleport to="body">
    <UserCardModal
      v-if="showUserModal"
      :show="showUserModal"
      :userId="selectedUserId"
      @close="closeUserModal"
    />

    <EditUserGroupForm
      v-if="showEditModal && association"
      :association="association"
      @close="showEditModal = false"
      @refresh="handleEditRefresh"
    />

    <DeleteUserGroupForm
      v-if="showDeleteModal && association"
      :association="association"
      @close="handleDeleteClose"
      @refresh="handleDeleteClose"
      @notify="handleDeleteNotify"
    />
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

.usergroup-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 650px;
  position: relative;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
}

/* Os botões do cabeçalho continuam sendo clique, não alça */
.modal-header--draggable .modal-close-btn,
.modal-header--draggable .modal-header-actions button {
  cursor: pointer;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

/* body mais compacto */
.card-content-grid {
  padding: 1rem;
}

.details-col {
  width: 100%;
}

/* título no “peso” do TextCardModal */
.title-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 6px;
}

/* ── Botão Editar ── */
.btn-edit-usergroup {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--info-border);
  background-color: var(--info-bg);
  color: var(--info-text);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-edit-usergroup i {
  font-size: 0.6rem;
}
.btn-edit-usergroup:hover {
  background-color: var(--info-border);
  transform: translateY(-1px);
}

/* ── Botão Apagar ── */
.btn-delete-usergroup {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--danger-bg);
  background-color: var(--danger-bg);
  color: var(--danger-text);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-delete-usergroup i {
  font-size: 0.6rem;
}
.btn-delete-usergroup:hover {
  background-color: var(--danger-bg);
  transform: translateY(-1px);
}

/* ── Tooltip ── */
.tooltip-bottom {
  position: relative;
}
.tooltip-bottom::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%) translateY(-2px);
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1;
  padding: 5px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
  z-index: 80;
}
.tooltip-bottom:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  transition-delay: 0.8s;
}

.text-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-dark);
  line-height: 1.25;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.meta-grid {
  grid-template-columns: 1fr 1fr;
}

.meta-label {
  margin-bottom: 2px;
}

/* mantém o destaque do grupo */
.group-value {
  font-weight: 600;
  color: #4f46e5; /* indigo-600 */
}

.footer-info {
  padding-top: 6px;
  width: 100%;
}

.users-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
}

</style>
